import { Timestamp } from 'firebase/firestore';
import { BedDouble, BellRing, BrushCleaning, Car, Clock, ConciergeBell, HelpCircle, PackagePlus, Plane, Settings } from 'lucide-react';

import { i18n } from '@/utils/i18n';

export type TaskStatuses = 'new' | 'inProgress' | 'completed' | 'canceled';
export type TaskPriority = 'high' | 'medium' | 'normal';
/**
 * 7 kategorii tasków — 6 z chatu AI:
 *  - `technical` (awaria), `housekeeping` (obsługa sprzątająca),
 *  - `wakeup` (budzenie), `guestAsk` (recepcja),
 *  - `airportTransfer` (transfer na lotnisko), `taxi` (taksówka pod adres)
 * + legacy `others` dla ręcznie tworzonych zgłoszeń bez wyraźnej kategorii.
 */
export type TaskCategory = 'technical' | 'housekeeping' | 'wakeup' | 'guestAsk' | 'airportTransfer' | 'taxi' | 'lateCheckout' | 'minibar' | 'pillowMenu' | 'others';
export type ReporterType = 'staff' | 'guest';

export const getTaskCategoryLabels = (): Record<TaskCategory | 'all', string> => ({
  technical: i18n.t('reports.category.technical'),
  housekeeping: i18n.t('reports.category.housekeeping'),
  wakeup: i18n.t('reports.category.wakeup', 'Budzenie'),
  guestAsk: i18n.t('reports.category.guestAsk', 'Prośba do recepcji'),
  airportTransfer: i18n.t('reports.category.airportTransfer', 'Transfer na lotnisko'),
  taxi: i18n.t('reports.category.taxi', 'Taksówka'),
  lateCheckout: i18n.t('reports.category.lateCheckout', 'Późne wymeldowanie'),
  minibar: i18n.t('reports.category.minibar', 'Mini bar'),
  pillowMenu: i18n.t('reports.category.pillowMenu', 'Menu poduszek'),
  others: i18n.t('reports.category.others'),
  all: i18n.t('reports.category.all'),
});

export const TaskCategoryColors: Record<TaskCategory, string> = {
  technical: '#dc2626',
  housekeeping: '#7c3aed',
  wakeup: '#0284c7',
  guestAsk: '#ea580c',
  airportTransfer: '#0b91b2',
  taxi: '#f59e0b',
  lateCheckout: '#4f46e6',
  minibar: '#db2878',
  pillowMenu: '#0d9488',
  others: '#6b7280',
};

export const TaskHistoryCategoryColors: Record<TaskCategory, string> = {
  technical: '#feedec',
  housekeeping: '#f3eeff',
  wakeup: '#e0f2fe',
  guestAsk: '#fed7aa',
  airportTransfer: '#cffafe',
  taxi: '#fef3c7',
  lateCheckout: '#e0e7ff',
  minibar: '#fce7f3',
  pillowMenu: '#ccfbf1',
  others: '#f3f4f6',
};

export const taskCategoryIcons = {
  technical: Settings,
  housekeeping: BrushCleaning,
  wakeup: BellRing,
  guestAsk: ConciergeBell,
  airportTransfer: Plane,
  taxi: Car,
  lateCheckout: Clock,
  minibar: PackagePlus,
  pillowMenu: BedDouble,
  others: HelpCircle,
};

export const getTaskPriorityLabels = (): Record<TaskPriority, string> => ({
  normal: i18n.t('reports.priority.normal'),
  medium: i18n.t('reports.priority.medium'),
  high: i18n.t('reports.priority.high'),
});

export const TaskPriorityBackgrounds: Record<TaskPriority, string> = {
  normal: '#eff6ff',
  medium: '#fffbeb',
  high: '#ef4444',
};

export const TaskPriorityColors: Record<TaskPriority, string> = {
  normal: '#3b82f6',
  medium: '#f59e0b',
  high: '#FFF',
};

export const TaskStatusBackgrounds: Record<TaskStatuses, string> = {
  new: '#eff6ff',
  inProgress: '#fffbeb',
  completed: '#bbf7d0',
  canceled: '#fecaca',
};

export const TaskStatusColors: Record<TaskStatuses, string> = {
  new: '#3b82f6',
  inProgress: '#f59e0b',
  completed: '#22c55e',
  canceled: '#ef4444',
};

export const getTaskStatusLabels = (): Record<TaskStatuses, string> => ({
  new: i18n.t('reports.status.new'),
  inProgress: i18n.t('reports.status.inProgress'),
  completed: i18n.t('reports.status.completed'),
  canceled: i18n.t('reports.status.canceled'),
});

export const getReporterTypeLabels = (): Record<ReporterType, string> => ({
  guest: i18n.t('reports.reporterType.guest'),
  staff: i18n.t('reports.reporterType.staff'),
});

export const ReporterTypeBackgrounds: Record<ReporterType, string> = {
  guest: '#fef3c7',
  staff: '#dbeafe',
};

export const ReporterTypeColors: Record<ReporterType, string> = {
  guest: '#d97706',
  staff: '#2563eb',
};

export interface Task {
  id: string;
  name: string;
  description: string;
  room?: number;
  customLocation?: string;
  reportedBy?: string;
  reporterName?: string;
  reporterType?: ReporterType;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  category: TaskCategory;
  priority: TaskPriority;
  status: TaskStatuses;
  image?: string;
  completedAt?: Timestamp;
  // Czas ostatniej zmiany statusu + mapa „kiedy osiągnięto dany status"
  // (statusTimestamps.inProgress = kiedy zgłoszenie zostało podjęte).
  statusChangedAt?: Timestamp;
  statusTimestamps?: Partial<Record<TaskStatuses, Timestamp>>;
  supervisorId?: string;
  isArchived?: boolean;
  archivedAt?: Timestamp;
}

// `null` = pole świadomie puste (backend je czyści przy edycji). Formularz nie
// wysyła `undefined`, bo JSON.stringify wycina takie klucze i przy edycji stara
// wartość zostawałaby w dokumencie.
export interface TaskFirebaseRequest {
  name: string;
  description: string;
  room?: number | null;
  customLocation?: string | null;
  reporterName?: string;
  reporterType: ReporterType;
  reporterId?: string | null;
  lang: string;
  category: TaskCategory;
  priority?: TaskPriority;
  status: TaskStatuses;
  image?: string | null;
  supervisorId?: string | null;
  internalOrder?: boolean; // If true, task is from panel - don't create inApp notification
}

export interface TaskDocumentRequest {
  hotelId: string;
  data: TaskFirebaseRequest;
  performedBy: string;
}

/**
 * Body `v3-updateTicket` (PUT) — treść zgłoszenia, status, przypisanie opiekuna
 * i archiwizacja. Wszystko opcjonalne: pominięte pole = „nie ruszaj", `null` czyści.
 * `performedBy` NIE jedzie z body: wykonawcę backend bierze z tokenu.
 */
export interface UpdateTicketRequest {
  hotelId: string;
  taskId: string;
  data: {
    name?: string;
    description?: string;
    room?: number | null;
    customLocation?: string | null;
    category?: TaskCategory;
    priority?: TaskPriority;
    image?: string | null;
    reporterName?: string | null;
    reporterId?: string | null;
    reporterType?: ReporterType;
    lang?: string;
    status?: TaskStatuses;
    /** `null` odpina opiekuna. */
    supervisorId?: string | null;
    isArchived?: boolean;
  };
}

export interface TaskDataModel {
  data: Task[];
  totalPages: number;
  isLoading: boolean;
  error: Error | null;
}

export type TaskFilterAssignment = 'all' | 'assigned' | 'unassigned';

// Task History types
export type TaskHistoryActionType =
  | 'task_created'
  | 'task_updated'
  | 'task_archived'
  | 'task_restored'
  | 'supervisor_assigned'
  | 'supervisor_removed'
  | 'supervisor_changed'
  | 'status_changed';

export interface TaskHistoryEntry {
  id: string;
  action: TaskHistoryActionType;
  performedBy: string;
  // For supervisor changes
  previousSupervisorId?: string | null;
  newSupervisorId?: string | null;
  // For status changes
  previousStatus?: TaskStatuses;
  newStatus?: TaskStatuses;
  // For task_updated — lista pól treści zmienionych w danym zapisie formularza.
  changedFields?: string[];
  createdAt: Timestamp;
}

export const getTaskHistoryActionLabels = (): Record<TaskHistoryActionType, string> => ({
  task_created: i18n.t('reports.history.taskCreated'),
  task_updated: i18n.t('reports.history.taskUpdated'),
  task_archived: i18n.t('reports.history.taskArchived'),
  task_restored: i18n.t('reports.history.taskRestored'),
  supervisor_assigned: i18n.t('reports.history.supervisorAssigned'),
  supervisor_removed: i18n.t('reports.history.supervisorRemoved'),
  supervisor_changed: i18n.t('reports.history.supervisorChanged'),
  status_changed: i18n.t('reports.history.statusChanged'),
});

export interface GetTaskHistoryRequest {
  hotelId: string;
  taskId: string;
}
